// @flow
/* eslint-disable max-lines-per-function */
import * as React from 'react'
import { sum } from 'lodash'

import { getRateLowestPrice } from '@/helpers'
import { Tooltip } from '@/components'
import Strings, { MERCHANT_FEE } from '@/constants'
import type { PricingDataType, RateDataType } from '@/models/property'

import styles from './styles.scss'

export type PriceProps = {
  // eslint-disable-next-line
  rate?: RateDataType,
  // eslint-disable-next-line
  pricing?: PricingDataType,
  // eslint-disable-next-line
  title?: string,
  // eslint-disable-next-line
  hideRefundableLabel?: boolean,
}

const PriceComponent = ({rate = null, pricing: pricingProp = null, hideRefundableLabel = false, title = ''}: PriceProps) => {
  const pricing = React.useMemo(() => pricingProp || getRateLowestPrice(rate), [pricingProp, rate])
  // $FlowFixMe
  const inclusivePrice = React.useMemo(() => pricing?.totals?.inclusive?.billable_currency?.value, [pricing])
  // $FlowFixMe
  const mandatoryFee = React.useMemo(() => pricing?.fees?.mandatory_fee?.billable_currency?.value, [pricing])
  // $FlowFixMe
  const mandatoryTax = React.useMemo(() => pricing?.fees?.mandatory_tax?.billable_currency?.value, [pricing])
  // $FlowFixMe
  const resortFee = React.useMemo(() => pricing?.fees?.resort_fee?.billable_currency?.value, [pricing])

  const nightlyValues = React.useMemo(() => {
    // $FlowFixMe
    const nightly = pricing?.nightly || []
    const values = {
      base_rate: [],
      tax_and_service_fee: [],
      property_fee: [],
      sales_tax: [],
      recovery_charges_and_fees: [],
      traveler_service_fee: [],
      adjustment: [],
      extra_person_fee: [],
      compensation: []
    }
    // $FlowFixMe
    nightly.forEach((night: {[string]: {type: string, value: string, currency: string}}) => {
      // $FlowFixMe
      Object.values(night).forEach((value: {type: string, value: string, currency: string}) => {
        if (Object.keys(values).includes(value.type)) {
          values[value.type] = [...values[value.type], parseFloat(value.value) * MERCHANT_FEE]
        }
      })
    })

    return values
    // $FlowFixMe
  }, [pricing?.nightly])

  const stayValues = React.useMemo(() => {
    // $FlowFixMe
    const staying = pricing?.stay || []
    const values = {
      base_rate: [],
      tax_and_service_fee: [],
      property_fee: [],
      sales_tax: [],
      recovery_charges_and_fees: [],
      traveler_service_fee: [],
      adjustment: [],
      extra_person_fee: [],
      compensation: []
    }
    // $FlowFixMe
    staying.forEach((item: {[string]: {type: string, value: string, currency: string}}) => {
      if (Object.keys(values).includes(item.type)) {
        values[item.type] = [...values[item.type], parseFloat(item.value) * MERCHANT_FEE]
      }
    })

    return values
    // $FlowFixMe
  }, [pricing?.stay])

  const baseTotalFee = React.useMemo(() => sum(nightlyValues.base_rate), [nightlyValues])

  const averageNightlyFee = React.useMemo(() =>
    Math.round(baseTotalFee / nightlyValues.base_rate.length),
    [baseTotalFee, nightlyValues]
  )

  const totalToPay = React.useMemo(() => inclusivePrice
    ? parseFloat(inclusivePrice) * MERCHANT_FEE
    : null,
  [inclusivePrice])

  const propertyFees = mandatoryFee || mandatoryTax || resortFee
    ? (
      <div className={styles.propertyFees}>
        <span className={styles.feesTitle}>{Strings.infoCard.feesTitle}</span>
        { mandatoryFee && (
          <div className={styles.feeItem}>
            <span>{Strings.infoCard.mandatoryFee}</span>
            <span>{`$${mandatoryFee}`}</span>
          </div>
        )}
        { mandatoryTax && (
          <div className={styles.feeItem}>
            <span>{Strings.infoCard.mandatoryTax}</span>
            <span>{`$${mandatoryTax}`}</span>
          </div>
        )}
        { resortFee && (
          <div className={styles.feeItem}>
            <span>{Strings.infoCard.resortFee}</span>
            <span>{`$${resortFee}`}</span>
          </div>
        )}
      </div>
    ) : null

  return totalToPay && averageNightlyFee ? (
    <>
      {title && <h4>{title}</h4>}
      <div className={styles.priceContainer}>
        <div className={styles.priceSection}>
          <div>
          <div className={styles.priceTotal}>
              <span>{Strings.infoCard.price}</span>
              <span className={styles.priceNumber}>{`$${averageNightlyFee}`}</span>
            </div>
            <div className={styles.priceBreakdown}>
              <div className={styles.priceItem}>
                <span>{Strings.infoCard.baseRate}</span>
                <span>{`$${(baseTotalFee).toFixed(2)}`}</span>
              </div>
              {nightlyValues.extra_person_fee.length > 0 && (
                <div className={styles.priceItem}>
                  <span>{Strings.infoCard.extraPersonFee}</span>
                  <span>
                    {// $FlowFixMe
                    `$${(
                      sum(nightlyValues.extra_person_fee)
                      + sum(stayValues.extra_person_fee)
                    ).toFixed(2)}`
                    }
                  </span>
                </div>
              )}
              {nightlyValues.tax_and_service_fee.length > 0 && (
                <div className={styles.priceItem}>
                  <Tooltip
                    content={<p>{Strings.infoCard.taxAndServiceFeeCopy}</p>}
                    minWidth="250px"
                  >
                    <span>{Strings.infoCard.taxAndServiceFee} ⓘ</span>
                  </Tooltip>
                  <span>
                    {// $FlowFixMe
                    `$${(
                      sum(nightlyValues.tax_and_service_fee)
                      + sum(stayValues.tax_and_service_fee)
                    ).toFixed(2)}`
                    }
                  </span>
                </div>
              )}
              {nightlyValues.property_fee.length > 0 && (
                <div className={styles.priceItem}>
                  <span>{Strings.infoCard.propertyFee}</span>
                  <span>
                    {// $FlowFixMe
                    `$${(
                      sum(nightlyValues.property_fee)
                      + sum(stayValues.property_fee)
                    ).toFixed(2)}`
                    }
                  </span>
                </div>
              )}
              {nightlyValues.sales_tax.length > 0 && (
                <div className={styles.priceItem}>
                  <span>{Strings.infoCard.salesTax}</span>
                  <span>
                    {// $FlowFixMe
                    `$${(
                      sum(nightlyValues.sales_tax)
                      + sum(stayValues.sales_tax)
                    ).toFixed(2)}`
                    }
                  </span>
                </div>
              )}
              {nightlyValues.recovery_charges_and_fees.length > 0 && (
                <div className={styles.priceItem}>
                  <span>{Strings.infoCard.recoveryChargesAndFees}</span>
                  <span>
                    {// $FlowFixMe
                    `$${(
                      sum(nightlyValues.recovery_charges_and_fees)
                      + sum(stayValues.recovery_charges_and_fees)
                    ).toFixed(2)}`
                    }
                  </span>
                </div>
              )}
              <div className={styles.priceItem}>
                <span>{Strings.infoCard.total}</span>
                <span>
                  {// $FlowFixMe
                  `$${(totalToPay).toFixed(2)}`
                  }
                </span>
              </div>
            </div>
          </div>
          {(!hideRefundableLabel || propertyFees) && (
            <div className={styles.propertyAndRefundable}>
              {propertyFees}
              {rate && !hideRefundableLabel && !rate?.refundable && (
                <div className={styles.priceNonRefundable}>
                  <span>{Strings.infoCard.nonRefundable}</span>
                </div>
              )}
              {rate && !hideRefundableLabel && rate?.refundable && (
                <div className={styles.priceRefundable}>
                  <span>{Strings.infoCard.refundable}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  ) : null
}

export default PriceComponent
